/**
 * Age calculation utilities for date_of_birth
 * Replaces static age integer with dynamic age calculation
 */

/**
 * Calculate current age from date of birth
 * @param dateOfBirth - Date of birth (Date object or date string)
 * @returns Current age in years, or null if invalid date
 */
export function calculateCurrentAge(dateOfBirth: Date | string | null | undefined): number | null {
  if (!dateOfBirth) return null;
  
  const birthDate = typeof dateOfBirth === 'string' ? new Date(dateOfBirth) : dateOfBirth;
  if (isNaN(birthDate.getTime())) return null;
  
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  // Adjust if birthday hasn't occurred this year
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
}

/**
 * Calculate age on a specific date from date of birth
 * @param dateOfBirth - Date of birth (Date object or date string)
 * @param targetDate - Date to calculate age at (Date object or date string)
 * @returns Age in years on the target date, or null if invalid dates
 */
export function calculateAgeOnDate(
  dateOfBirth: Date | string | null | undefined,
  targetDate: Date | string
): number | null {
  if (!dateOfBirth) return null;
  
  const birthDate = typeof dateOfBirth === 'string' ? new Date(dateOfBirth) : dateOfBirth;
  const target = typeof targetDate === 'string' ? new Date(targetDate) : targetDate;
  
  if (isNaN(birthDate.getTime()) || isNaN(target.getTime())) return null;
  
  let age = target.getFullYear() - birthDate.getFullYear();
  const monthDiff = target.getMonth() - birthDate.getMonth();
  
  // Adjust if birthday hasn't occurred in the target year
  if (monthDiff < 0 || (monthDiff === 0 && target.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return Math.max(0, age); // Age cannot be negative
}

/**
 * Format age for display
 * @param age - Age in years (number or null)
 * @returns Formatted string like "5 years old" or "N/A"
 */
export function formatAge(age: number | null | undefined): string {
  if (age === null || age === undefined) return 'N/A';
  return `${age} ${age === 1 ? 'year' : 'years'} old`;
}

/**
 * Validate date of birth (must be in past and reasonable)
 * @param dateOfBirth - Date to validate
 * @param minAge - Minimum age (default 0)
 * @param maxAge - Maximum age (default 18 for camp eligibility)
 * @returns Validation result with isValid boolean and error message
 */
export function validateDateOfBirth(
  dateOfBirth: Date | string | null | undefined,
  minAge: number = 0,
  maxAge: number = 18
): { isValid: boolean; error?: string } {
  if (!dateOfBirth) {
    return { isValid: false, error: 'Date of birth is required' };
  }
  
  const birthDate = typeof dateOfBirth === 'string' ? new Date(dateOfBirth) : dateOfBirth;
  if (isNaN(birthDate.getTime())) {
    return { isValid: false, error: 'Invalid date format' };
  }
  
  const today = new Date();
  if (birthDate > today) {
    return { isValid: false, error: 'Date of birth cannot be in the future' };
  }
  
  const age = calculateCurrentAge(birthDate);
  if (age === null) {
    return { isValid: false, error: 'Unable to calculate age' };
  }
  
  if (age < minAge) {
    return { isValid: false, error: `Age must be at least ${minAge} years` };
  }
  
  if (age > maxAge) {
    return { isValid: false, error: `Age cannot exceed ${maxAge} years` };
  }
  
  return { isValid: true };
}

